import { useCallback, useEffect, useState } from 'react';

import { STORY_GENERATED_CONTENT_SUBTYPE } from '../../../../../../constants';

const useImageReady = (src) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(false);
  }, [src]);

  const imageRef = useCallback((node) => {
    if (node?.complete && node.naturalWidth > 0) {
      setIsReady(true);
    }
  }, []);

  const onImageLoad = useCallback(() => {
    setIsReady(true);
  }, []);

  const onImageError = useCallback(() => {
    setIsReady(false);
  }, []);

  return {
    isReady,
    imageProps: { ref: imageRef, onLoad: onImageLoad, onError: onImageError },
  };
};

const useVideoReady = (src) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(false);
  }, [src]);

  const videoRef = useCallback((node) => {
    if (node?.readyState >= HTMLMediaElement.HAVE_METADATA) {
      setIsReady(true);
    }
  }, []);

  const onVideoLoadedData = useCallback(() => {
    setIsReady(true);
  }, []);

  const onVideoError = useCallback(() => {
    setIsReady(false);
  }, []);

  return {
    isReady,
    videoProps: { ref: videoRef, onLoadedData: onVideoLoadedData, onError: onVideoError },
  };
};

const useStoryGeneratedImage = ({ src, subtype }) => {
  const [hasError, setHasError] = useState(false);

  const handleError = useCallback(() => {
    setHasError(true);
  }, []);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  const isVideo = subtype === STORY_GENERATED_CONTENT_SUBTYPE.MP4;
  const mediaUrl = src || null;

  const { isReady: isImageReady, imageProps: baseImageProps } = useImageReady(isVideo ? null : mediaUrl);
  const { isReady: isVideoReady, videoProps: baseVideoProps } = useVideoReady(isVideo ? mediaUrl : null);

  const imageProps = {
    ...baseImageProps,
    onError: (event) => {
      baseImageProps.onError?.(event);
      handleError();
    },
  };

  const videoProps = {
    ...baseVideoProps,
    onError: (event) => {
      baseVideoProps.onError?.(event);
      handleError();
    },
  };

  const isMediaReady = isVideo ? isVideoReady : isImageReady;
  const isLoading = Boolean(mediaUrl) && !hasError && !isMediaReady;

  return {
    mediaUrl: hasError ? null : mediaUrl,
    error: hasError,
    isLoading,
    imageProps,
    videoProps,
  };
};

export default useStoryGeneratedImage;
